export type TreeNode<T extends MyRecord, U extends string = 'children'> = T & {
  [field in U]?: TreeNode<T, U>[];
};

// To Tree
export type Relation<T extends MyRecord> = (T | [T, Relation<T>?])[];

export function toTree<T extends MyRecord>(
  relation: Relation<T>
): TreeNode<T, 'children'>[];

export function toTree<T extends MyRecord, U extends string>(
  relation: Relation<T>,
  childrenField: U
): TreeNode<T, U>[];

export function toTree<T extends MyRecord, R extends MyRecord>(
  relation: Relation<T>,
  cb: (item: T) => R
): TreeNode<R, 'children'>[];

export function toTree<T extends MyRecord, U extends string, R extends MyRecord>(
  relation: Relation<T>,
  cb: (item: T) => R,
  childrenField: U
): TreeNode<R, U>[];

export function toTree(
  relation: Relation<MyRecord>,
  cb?: (item: MyRecord) => any,
  childrenField?: string
) {
  if (typeof cb === 'string') {
    cb = undefined;
    childrenField = cb;
  }

  childrenField = childrenField || 'children';

  return relation.map((r) => {
    const [item, children] = Array.isArray(r) ? r : [r];
    let data_ = {
      ...item,
    };

    data_ = cb ? cb(data_) : data_;

    if (children) {
      data_[childrenField] = cb
        ? toTree(children, cb, childrenField)
        : toTree(children, childrenField);
    }

    return data_;
  });
}

// Filter Tree
export type FilterTreeCallback<T extends MyRecord, U extends string> = (
  node: TreeNode<T, U>
) => ((node: TreeNode<T, U>) => any) | boolean;

export function filterTree<T extends MyRecord>(
  data: TreeNode<T, 'children'>[],
  cb: FilterTreeCallback<T, 'children'>
): TreeNode<T, 'children'>[];

export function filterTree<T extends MyRecord, U extends string>(
  data: TreeNode<T, U>[],
  cb: FilterTreeCallback<T, U>,
  childrenField: U
): TreeNode<T, U>[];

export function filterTree(
  data: TreeNode<MyRecord, string>[],
  cb: FilterTreeCallback<any, string>,
  childrenField = 'children'
) {
  const data_ = [];
  for (const node of data) {
    const result = cb(node);
    if (result) {
      const node_ = { ...node };
      const children = node_[childrenField];
      if (Array.isArray(children)) {
        node_[childrenField] = filterTree(children, cb, childrenField);
      }
      if (typeof result !== 'function' || result(node_)) {
        data_.push(node_);
      }
    }
  }
  return data_;
}

// Map tree
export type MapTreeCallback<T extends MyRecord, U extends string> = (
  node: TreeNode<T, U>
) => any;

export function mapTree<T extends MyRecord>(
  data: TreeNode<T, 'children'>[],
  cb: MapTreeCallback<T, 'children'>
): TreeNode<T, 'children'>[];

export function mapTree<T extends MyRecord, U extends string>(
  data: TreeNode<T, U>[],
  cb: MapTreeCallback<T, U>,
  childrenField: U
): TreeNode<T, U>[];

export function mapTree(
  data: TreeNode<MyRecord, string>[],
  cb: MapTreeCallback<any, string>,
  childrenField = 'children'
) {
  const data_ = [];
  for (const node of data) {
    const result = cb(node);
    if (result) {
      const children = result?.[childrenField];
      if (Array.isArray(children)) {
        result[childrenField] = mapTree(children, cb, childrenField);
      }
      data_.push(result);
    }
  }
  return data_;
}

// Each tree
export interface EachTreeContext<T extends MyRecord, U extends string> {
  index: number;
  parentIndex: number;
  parent: TreeNode<T, U> | null;
  parentsIndex: number[];
  parents: TreeNode<T, U>[];
}

export type EachTreeCallback<T extends MyRecord, U extends string> = (
  node: TreeNode<T, U>,
  context: EachTreeContext<T, U>
) => boolean | void;

export function eachTree<T extends MyRecord>(
  data: TreeNode<T, 'children'>[],
  cb: EachTreeCallback<T, 'children'>
): void;

export function eachTree<T extends MyRecord, U extends string>(
  data: TreeNode<T, U>[],
  cb: EachTreeCallback<T, U>,
  childrenField: U,
  context?: EachTreeContext<T, U>
): void;

export function eachTree(
  data: TreeNode<MyRecord, string>[],
  cb: EachTreeCallback<any, string>,
  childrenField = 'children',
  context: EachTreeContext<any, string> = {
    index: -1,
    parentIndex: -1,
    parent: null,
    parentsIndex: [],
    parents: [],
  }
) {
  for (let i = 0; i < data.length; i++) {
    const node = data[i];

    const context_: EachTreeContext<any, string> = {
      ...context,
      index: i,
    };

    if (cb(node, context_)) {
      return true;
    }

    const children = node[childrenField];

    if (
      Array.isArray(children) &&
      eachTree(children, cb, childrenField, {
        index: -1,
        parentIndex: i,
        parent: node,
        parentsIndex: context_.parentsIndex.concat(i),
        parents: context_.parents.concat(node),
      })
    ) {
      return true;
    }
  }
}
